import { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { dedupeRequest, getCacheKey } from '../utils/queryDeduplication';

export const useUserProfile = (userProfile) => {
  const [user, setUser] = useState({
    username: '',
    profilePictureUrl: ''
  });

  useEffect(() => {
    const loadUserProfile = async () => {
      if (userProfile) {
        setUser({
          username: userProfile.username || '',
          profilePictureUrl: userProfile.profilePictureUrl || userProfile.profile_picture_url || ''
        });
        return;
      }

      try {
        // Use getSession() instead of getUser() to avoid network requests
        const { data: { session } } = await supabase.auth.getSession();
        const authUser = session?.user;
        if (!authUser) return;

        // First, try to get from sessionStorage cache (set by Home.js/TopNavigation.js)
        const cachedProfile = sessionStorage.getItem(`userProfile_${authUser.id}`);
        if (cachedProfile) {
          try {
            const parsed = JSON.parse(cachedProfile);
            setUser({
              username: parsed.username || '',
              profilePictureUrl: parsed.profilePictureUrl || parsed.profile_picture_url || ''
            });
            return; // Use cached data
          } catch (e) {
            // Invalid cache, continue to fetch
          }
        }

        // Use same cache key as Home/TopNavigation to share the request
        const profileCacheKey = getCacheKey('fetchUserProfile', authUser.id);
        const userData = await dedupeRequest(profileCacheKey, async () => {
          const { data, error } = await supabase
            .from('user_profiles')
            .select('username, profile_picture_url, gender')
            .eq('user_id', authUser.id)
            .maybeSingle();
          if (error) throw error;
          return data;
        }, { keepAliveMs: 2000, caller: 'useUserProfile' });
          
        if (userData) {
          setUser({
            username: userData.username || '',
            profilePictureUrl: userData.profile_picture_url || ''
          });
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
      }
    };

    loadUserProfile();
  }, [userProfile]);

  return user;
};
